import { Link } from "react-router-dom";
import PageHero from "@/components/PageHero";
import Reveal from "@/components/Reveal";
import { FOUNDER_IMAGE, CERTIFICATIONS, PRIMARY_CTA, BRAND, STAFF, CBE_BADGE, BRAND_LOGO_NAVY, IMAGES } from "@/lib/data";

const About = () => {
  return (
    <main data-testid="page-about">
      <PageHero
        eyebrow="About"
        title="Relocation is more than logistics. It is dignity, delivered on schedule."
      />

      {/* Founder bio · primary focus on Ismael's experience and background */}
      <section className="section bg-white" data-testid="about-founder">
        <div className="container-kc">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
            {/* Smaller founder photo per client feedback */}
            <div className="lg:col-span-4">
              <Reveal>
                <div className="relative max-w-xs">
                  <div className="aspect-[4/5] overflow-hidden bg-gray-100">
                    <img
                      src={FOUNDER_IMAGE}
                      alt="Ismael Kassim, Founder & CEO of Kassim Consulting"
                      className="w-full h-full object-cover"
                      data-testid="founder-image"
                    />
                  </div>
                  <div className="mt-4">
                    <div className="eyebrow eyebrow--gold">Founder &amp; CEO</div>
                    <div className="font-serif text-xl text-[#000000] mt-1">{BRAND.founder}</div>
                  </div>
                </div>
              </Reveal>
            </div>

            <div className="lg:col-span-8">
              <Reveal delay={0.1}>
                <div className="eyebrow eyebrow--gold mb-4">Background &amp; Experience</div>
                <h2 className="h-display text-3xl md:text-5xl">A practitioner first.</h2>
                <div className="hairline--gold mt-6 mb-10" />
                <div className="space-y-6 text-gray-700 text-base md:text-[17px] leading-relaxed">
                  <p>
                    Ismael Kassim is the founder and CEO of Kassim Consulting. He built the firm in 2022 after years of leading complex projects at the intersection of compliance, infrastructure, and operational logistics, work that taught him a simple truth: the hardest part of a relocation isn&apos;t the strategy on paper, it&apos;s the execution on the ground.
                  </p>
                  <p>
                    He holds a Housing Quality Standards (HQS) certification and works fluently in HUD&apos;s URA Handbook 1378, the regulatory backbone of nearly every federally funded relocation in the country. He brings a rigorous operational background to every engagement: clear planning, tight documentation, and a bias toward shipping.
                  </p>
                  <p>
                    Since launching Kassim Consulting, he has led the relocation of more than 1,500 households across DC, Maryland, and Virginia, working with developers, housing authorities, and mission-driven organizations on projects up to 240 units.
                  </p>
                  <p>
                    What defines the work: a refusal to separate strategy from execution. Ismael doesn&apos;t hand off the plan and disappear. He stays through the move, the close-out, and the audit. That&apos;s the standard.
                  </p>
                </div>

                <div className="mt-12">
                  <div className="eyebrow eyebrow--gold mb-4">Certifications</div>
                  <div className="flex flex-wrap gap-3">
                    {CERTIFICATIONS.map((c) => (
                      <span key={c.code} className="cert-chip" data-testid={`about-cert-${c.code.toLowerCase()}`}>
                        {c.code}
                      </span>
                    ))}
                  </div>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* DC visual break */}
      <section className="bg-white" data-testid="about-dc-banner">
        <div className="container-kc py-12 md:py-16">
          <Reveal>
            <div className="aspect-[21/9] overflow-hidden bg-gray-100">
              <img src={IMAGES.aboutDC} alt="Washington, DC, our home base" className="w-full h-full object-cover" loading="lazy" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* Firm philosophy */}
      <section className="bg-[#F8F9FA]" data-testid="about-firm">
        <div className="container-kc py-24 md:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <Reveal className="lg:col-span-5">
              <div className="eyebrow eyebrow--gold mb-4">Our Firm</div>
              <h2 className="h-display text-3xl md:text-5xl leading-tight">
                One practice. One standard.
              </h2>
            </Reveal>
            <Reveal delay={0.1} className="lg:col-span-7 space-y-6 text-gray-700 text-base md:text-[17px] leading-relaxed">
              <p>
                Kassim Consulting is a Black-owned, mission-driven firm based in Washington, DC.
              </p>
              <p>
                We plan and execute <span className="text-[#000000] font-semibold">URA, HUD 1378, RAD, and Section 8 compliant relocations</span> for affordable and mixed-use housing, centering the lived experience of the people impacted by redevelopment while keeping projects on schedule.
              </p>
              <p>
                We bridge strategy, execution, and community outreach. We don&apos;t just advise. We deliver.
              </p>
              <div className="pt-6">
                <a
                  href="https://dslbd.dc.gov/page/certified-business-enterprise-cbe"
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-5 group"
                  data-testid="cbe-badge-about"
                  aria-label="DC Certified Business Enterprise Program"
                >
                  <img src={CBE_BADGE} alt="DC Certified Business Enterprise Program" className="h-24 w-auto object-contain transition-transform group-hover:scale-105" />
                  <span className="flex flex-col">
                    <span className="eyebrow eyebrow--gold">DC Government Certified</span>
                    <span className="font-serif text-[#000000] text-base mt-1 leading-snug max-w-[16ch]">Certified Business Enterprise Program</span>
                  </span>
                </a>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Team · teaser linking to dedicated /team subpage */}
      <section className="section bg-white" data-testid="about-team-teaser">
        <div className="container-kc">
          <Reveal>
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-12 items-end">
              <div className="lg:col-span-7">
                <div className="flex items-center gap-3 mb-6">
                  <span className="hairline--gold" />
                  <span className="eyebrow">Meet the Team</span>
                </div>
                <h2 className="h-display text-3xl md:text-5xl">The people behind the work.</h2>
              </div>
              <div className="lg:col-span-5">
                <p className="text-gray-600 leading-relaxed">
                  A small, senior team that owns the work end to end across DC, Maryland, and Virginia.
                </p>
              </div>
            </div>
          </Reveal>

          {/* Preview · faces only · clicking any leads to /team */}
          <Reveal delay={0.1}>
            <Link
              to="/team"
              data-testid="about-team-preview-link"
              className="block group"
            >
              <div className="grid grid-cols-3 gap-px bg-gray-200">
                {STAFF.map((member, idx) => (
                  <div key={idx} className="bg-white aspect-[4/5] relative overflow-hidden" data-testid={`about-team-preview-${idx}`}>
                    {member.image ? (
                      <img
                        src={member.image}
                        alt={member.name}
                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                        loading="lazy"
                      />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <img src={BRAND_LOGO_NAVY} alt="Kassim Consulting" className="h-16 w-auto opacity-60" />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-[#000000]/85 via-[#000000]/0 to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-5 md:p-6 text-white">
                      <div className="font-serif text-base md:text-2xl leading-snug">{member.name}</div>
                      <div className="text-[10px] md:text-[12px] tracking-[0.12em] uppercase text-white/80 mt-1 hidden sm:block">
                        {member.role.split(" · ")[1] || member.role}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-10 flex items-center justify-between gap-6">
                <p className="text-sm text-gray-500 max-w-md">
                  See bios and regional leadership for our DC, MD, and VA teams.
                </p>
                <span className="inline-flex items-center gap-2 text-[13px] tracking-[0.18em] uppercase font-semibold text-[#000000] group-hover:text-[#C5A880] transition-colors border-b border-[#000000] group-hover:border-[#C5A880] pb-1">
                  Meet the Team
                  <span className="transition-transform group-hover:translate-x-1">→</span>
                </span>
              </div>
            </Link>
          </Reveal>
        </div>
      </section>

      {/* Closing CTA */}
      <section className="bg-[#000000] text-white">
        <div className="container-kc py-24 md:py-32">
          <Reveal>
            <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
              <div className="md:col-span-8">
                <div className="eyebrow eyebrow--gold mb-4">Partner With Us</div>
                <h2 className="h-display h-display--inverse text-3xl md:text-5xl">
                  Bring your most complex project to a partner who has done this before.
                </h2>
              </div>
              <div className="md:col-span-4 flex md:justify-end">
                <Link to={PRIMARY_CTA.to} className="btn btn--inverse" data-testid="about-cta">
                  {PRIMARY_CTA.label} <span className="btn-arrow">→</span>
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
};

export default About;
